import { toast } from 'sonner';
import { downloadSpreadsheet } from '@/lib/spreadsheet';
import {
  buildBuilderReportHtml,
  buildBuilderSheet,
  type BuilderExportInput,
} from './report-builder-state';
import { downloadBuilderWorkbook } from './report-builder-xlsx';

export type BuiltReportExportFormat = 'xlsx' | 'csv' | 'pdf';

/**
 * A built report, out of the browser in any of the three formats.
 *
 * One function for the two screens that show a built report — the Builder and
 * the Reports screen's summarized shapes — so a spreadsheet downloaded from
 * either is the same file, and a fix to one cannot leave the other behind.
 * Everything comes from the RESULT the server returned, never from the form
 * that requested it: a document headed with a configuration that did not
 * produce its numbers is worse than no document. [HW-877]
 *
 * Two spreadsheet writers, because the two formats can carry different things.
 * Excel goes through ExcelJS so a group's total is painted as a total — the
 * flat sheet the `xlsx` package can produce gives a person's total and their
 * project rows identical weight, five lines of text with nothing to tell them
 * apart. A CSV holds no styling at all, so it uses the shared writer and says
 * what it can in text.
 *
 * Every failure is reported here rather than left to the caller. Callers are
 * click handlers nobody awaits, so a throw from a writer would otherwise become
 * an unhandled rejection: a click that does nothing and says nothing. The PDF's
 * print step runs on a timer, after this function has returned, so it carries
 * a guard of its own.
 */
export async function exportBuiltReport(
  format: BuiltReportExportFormat,
  input: BuilderExportInput
): Promise<void> {
  try {
    if (format === 'pdf') {
      exportPdf(input);
      return;
    }
    const sheet = buildBuilderSheet(input);
    if (format === 'xlsx') {
      await downloadBuilderWorkbook(sheet);
      return;
    }
    downloadSpreadsheet({
      // The grand total is a row here rather than a footer, because a CSV has
      // no footer — and a file whose purpose is adding hours up has to end
      // with what they came to.
      rows: [...sheet.rows, { cells: sheet.total, subtotal: false }],
      // Indexed rather than named: the sheet's shape follows the report's
      // grouping, which is chosen at run time, so there is no fixed row type
      // to key off. `buildBuilderSheet` aligns the two arrays by position.
      columns: sheet.columns.map((column, index) => ({
        header: column.header,
        width: column.width,
        value: (row: (typeof sheet.rows)[number]) => row.cells[index] ?? '',
      })),
      filename: sheet.filename,
      format,
      sheetName: 'Time report',
      // What shaped the report, as lines before the headings: a CSV has no
      // other place to say it, and without it the file of a filtered August
      // is indistinguishable from the whole company's. [HW-953]
      preamble: sheet.about.map((note) => [note.label, note.text]),
    });
  } catch (error) {
    // The toast can only say "try again"; without the log, the cause of a
    // reported failure is unavailable to anyone acting on the report.
    // eslint-disable-next-line no-console
    console.error('Report export failed', error);
    toast.error('Export failed. Please try again.');
  }
}

function exportPdf(input: BuilderExportInput): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error(
      'Your browser blocked the print window. Allow pop-ups for this site to export a PDF.'
    );
    return;
  }
  printWindow.document.write(buildBuilderReportHtml(input));
  printWindow.document.close();
  printWindow.focus();
  // The timer callback runs after `exportBuiltReport` has returned, outside its
  // try, so it carries its own — as the entry view's PDF does.
  setTimeout(() => {
    try {
      // Closing the tab within the delay is the user's own doing, not a failure
      // to report — and `print` is gone from a discarded context, so reaching
      // for it would throw and toast at them for it.
      if (printWindow.closed) return;
      printWindow.print();
      printWindow.close();
    } catch (error) {
      // print() blocks while its dialog is up, so the tab can be closed DURING
      // the call as well as before it — the same user action.
      if (printWindow.closed) return;
      // eslint-disable-next-line no-console
      console.error('Report export failed', error);
      toast.error('Export failed. Please try again.');
    }
  }, 250);
}
